import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { AgentInsightFeedView } from 'features/trend-detection/features/insight-agent/components/AgentInsightFeedView/AgentInsightFeedView'
import { useAgentInsightFeed } from 'features/trend-detection/features/insight-agent/hooks/use-agent-insight-feed'
import { useAgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import type { AgentInsightFeedItem } from 'features/trend-detection/features/insight-agent/types'
import { trendDetectionAgentInsightPath } from 'features/trend-detection/paths'

export interface AgentInsightFeedStats {
  totalCount: number
  isLoading: boolean
}

export interface AgentInsightFeedContainerProps {
  projectId: string | null
  enabled: boolean
  onStats?: (stats: AgentInsightFeedStats) => void
}

/** Wires the insight-agent feed hook to its view; owns navigation. */
export const AgentInsightFeedContainer: React.FC<
  AgentInsightFeedContainerProps
> = ({ projectId, enabled, onStats }) => {
  const navigate = useNavigate()
  const service = useAgentInsightService()
  const feed = useAgentInsightFeed({ service, projectId, enabled })

  useEffect(() => {
    onStats?.({ totalCount: feed.totalCount, isLoading: feed.isLoading })
  }, [feed.totalCount, feed.isLoading, onStats])

  const handleInsightClick = useCallback(
    (insight: AgentInsightFeedItem) => {
      if (!projectId) return
      navigate(trendDetectionAgentInsightPath(projectId, insight.uuid))
    },
    [navigate, projectId],
  )

  if (!enabled) return null

  return (
    <AgentInsightFeedView
      items={feed.items}
      totalCount={feed.totalCount}
      page={feed.page}
      isLoading={feed.isLoading}
      onPageChange={feed.onPageChange}
      onInsightClick={handleInsightClick}
    />
  )
}
